import { getRUM } from '@codametrix/client-observability';
import React, { useEffect, useMemo, useRef } from 'react';
import { SectionCard } from './section-card';
import { sliceSectionsFromBody } from './section-slicer';
import type { ViewAnnotation } from './cmx-document-content-adapter';
import { CmxDocumentContentBody } from './cmx-document-content-body';
import { MEDIA_TYPE_PLAIN } from './cmx-document-content-types';
import './styles/section-viewer.scss';

/** RUM action name for CDMX-20061's section-rendering performance AC. */
export const SECTION_RENDER_ACTION = 'cmx-document-content.sections.rendered';

export type SectionViewerProps = {
  /** The body Markdown. Absent for a `?body=reference` read. */
  body: string | null | undefined;
  /** Section annotations, adapted from a layer-4 read. */
  annotations: ViewAnnotation[];
  /**
   * Annotations to highlight within each section's content. Separate from
   * `annotations` because sections define the layout while spans decorate it;
   * empty until CDMX-20063.
   */
  spanAnnotations?: ViewAnnotation[];
  /** The body's media type (`body.mediaType`). Absent means Markdown. */
  mediaType?: string;
};

/**
 * Renders a CMX Document Content as section cards, in the order the sections
 * appear in the note. This is the CDMX-20061 view.
 *
 * Sections come from an L4 annotation payload. Where no payload exists the
 * document is not sectionable and the flat body is the correct fallback --
 * CDMX-20059's view -- rather than an error.
 */
export const SectionViewer: React.FC<SectionViewerProps> = ({
  body,
  annotations,
  spanAnnotations,
  mediaType
}) => {
  // Timestamp taken before slicing so the reported duration covers the slice
  // plus React's render and commit, which is what a coder actually waits for.
  const startedAt = useRef(0);

  const result = useMemo(() => {
    startedAt.current =
      typeof performance !== 'undefined' ? performance.now() : 0;

    if (!body) {
      return null;
    }

    const sliced = sliceSectionsFromBody(body, annotations, {
      plain: mediaType === MEDIA_TYPE_PLAIN
    });
    const sliceMs =
      typeof performance !== 'undefined'
        ? performance.now() - startedAt.current
        : 0;

    return { ...sliced, sliceMs };
  }, [body, annotations, mediaType]);

  const sectionCount = result?.slices.length ?? 0;

  useEffect(() => {
    if (!result) {
      return;
    }

    const totalMs =
      typeof performance !== 'undefined'
        ? performance.now() - startedAt.current
        : 0;

    getRUM().addAction(SECTION_RENDER_ACTION, {
      sectionCount,
      annotationCount: annotations.length,
      unclaimedCount: result.unclaimed.length,
      bodyLength: body?.length ?? 0,
      sliceMs: Math.round(result.sliceMs * 100) / 100,
      totalMs: Math.round(totalMs * 100) / 100
    });
  }, [result, sectionCount, annotations.length, body]);

  if (!body) {
    return (
      <div className="cmx-document-content-section-viewer__empty">
        No document content.
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { preamble, slices, unclaimed } = result;

  // No sections detected: the flat body is the honest rendering, not an error.
  if (slices.length === 0) {
    return (
      <div className="cmx-document-content-section-viewer">
        <div className="cmx-document-content-section-viewer__info-bar">
          No sections detected — showing the full document
        </div>
        <CmxDocumentContentBody
          text={body}
          annotations={spanAnnotations}
          mediaType={mediaType}
        />
      </div>
    );
  }

  return (
    <div className="cmx-document-content-section-viewer">
      <div className="cmx-document-content-section-viewer__info-bar">
        {slices.length} {slices.length === 1 ? 'section' : 'sections'} · in note
        order
      </div>

      {preamble && (
        <div className="cmx-document-content-section-viewer__preamble">
          <div className="cmx-document-content-section-viewer__preamble-label">
            Preamble
          </div>
          <CmxDocumentContentBody
            text={preamble}
            annotations={spanAnnotations}
            mediaType={mediaType}
          />
        </div>
      )}

      {slices.map((slice) => (
        <SectionCard
          key={`${slice.annotation.id}-${slice.start}`}
          title={slice.title}
          bodyMarkdown={slice.bodyMarkdown}
          annotations={spanAnnotations}
          mediaType={mediaType}
        />
      ))}

      {unclaimed.length > 0 && (
        <>
          <div className="cmx-document-content-section-viewer__divider">
            Outside any section ({unclaimed.length})
          </div>
          {unclaimed.map((gap) => (
            <div
              className="cmx-document-content-section-viewer__unclaimed"
              key={`unclaimed-${gap.start}`}
            >
              <CmxDocumentContentBody
                text={gap.text}
                annotations={spanAnnotations}
                mediaType={mediaType}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};
